import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  createTranslator,
  formatBilingualStreetName,
  getPreferredLocale,
  getRouteSuffixFromPath,
  isLocale,
  localePathForSuffix,
} from './locale.js'
import { trackLocaleChange } from '../lib/analytics.js'
import GoogleAnalytics from '../components/GoogleAnalytics.jsx'
import PageSeo from '../components/PageSeo.jsx'
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY } from './translations.js'

const LocaleContext = createContext(null)

export function LocaleProvider({ children }) {
  const { locale: localeParam } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const routeSuffix = getRouteSuffixFromPath(location.pathname)

  if (!isLocale(localeParam)) {
    const preferred = getPreferredLocale()
    const nextPath = localePathForSuffix(preferred, routeSuffix)
    return <Navigate to={{ pathname: nextPath, search: location.search }} replace />
  }

  const locale = localeParam
  const t = useMemo(() => createTranslator(locale), [locale])

  useEffect(() => {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    document.documentElement.lang = locale === 'zh' ? 'zh-HK' : 'en'
  }, [locale])

  const setLocale = useCallback(
    (nextLocale) => {
      if (!isLocale(nextLocale) || nextLocale === locale) return
      trackLocaleChange(nextLocale)
      const suffix = getRouteSuffixFromPath(location.pathname)
      navigate(
        { pathname: localePathForSuffix(nextLocale, suffix), search: location.search },
        { replace: true },
      )
    },
    [locale, location.pathname, location.search, navigate],
  )

  const toggleLocale = useCallback(
    () => setLocale(locale === 'zh' ? 'en' : 'zh'),
    [locale, setLocale],
  )

  const value = useMemo(
    () => ({
      locale,
      t,
      setLocale,
      toggleLocale,
      formatStreetName: formatBilingualStreetName,
    }),
    [locale, t, setLocale, toggleLocale],
  )

  return (
    <LocaleContext.Provider value={value}>
      <PageSeo />
      <GoogleAnalytics />
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return context
}

export function useLocalePath(suffix = '') {
  const { locale } = useLocale()
  if (!suffix) return `/${locale}`
  return `/${locale}/${suffix.replace(/^\//, '')}`
}
